/**
 * Collection Quick Add Modal Handler
 * Intercepts add-to-cart form submissions on collection product cards
 * and opens a modal with the product purchase UI instead.
 */

import { DialogComponent } from '@theme/dialog';

class CollectionQuickAddHandler {
  #cachedContent = new Map();
  #abortController = null;
  #triggerButton = null;

  constructor() {
    this.init();
  }

  init() {
    // Listen for form submissions on collection product cards in capture mode
    document.addEventListener('submit', this.handleFormSubmit.bind(this), true);
    
    // Also listen for button clicks as a fallback
    document.addEventListener('click', this.handleButtonClick.bind(this), true);
  }

  /**
   * Handles form submit events
   * @param {Event} event - The submit event
   */
  handleFormSubmit(event) {
    console.log('Collection quick-add: Submit event intercepted', event.target);
    
    // Find closest element with data-quick-add-trigger from event target
    const triggerElement = /** @type {HTMLElement} */ (event.target).closest('[data-quick-add-trigger="true"]');
    
    if (!triggerElement) {
      return; // Not a collection product card form
    }
    
    console.log('Collection quick-add: Found trigger element', triggerElement);
    
    // Get the form element
    const form = /** @type {HTMLFormElement} */ (event.target.closest('form'));
    if (!form) {
      return;
    }
    
    // Check if product has only default variant (single variant)
    const hasOnlyDefaultVariant = triggerElement.dataset.hasOnlyDefaultVariant === 'true';
    if (hasOnlyDefaultVariant) {
      console.log('Collection quick-add: Single variant product, allowing normal submission');
      return; // Allow normal submission for single-variant products
    }

    // Prevent form submission
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    console.log('Collection quick-add: Form submission prevented');

    // Get product handle
    const productHandle = triggerElement.dataset.productHandle;
    if (!productHandle) {
      console.error('Collection quick-add: Product handle not found');
      return;
    }

    // Store the button that triggered this for focus restoration
    const submitButton = form.querySelector('button[type="submit"]');
    this.#triggerButton = submitButton;

    this.openQuickAddModal(productHandle, form);
  }

  /**
   * Handles button click events (fallback for cases where form submit doesn't fire)
   * @param {Event} event - The click event
   */
  handleButtonClick(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target.closest('button[type="submit"]'));
    if (!button) return;

    const form = /** @type {HTMLFormElement} */ (button.closest('form'));
    if (!form) return;

    // Check for data-quick-add-trigger on form or its parent product-form-component
    const productFormComponent = form.closest('product-form-component');
    const hasQuickAddTrigger = form.hasAttribute('data-quick-add-trigger') || 
                               (productFormComponent && productFormComponent.hasAttribute('data-quick-add-trigger'));
    
    if (!hasQuickAddTrigger) {
      return; // Not a collection product card form
    }

    // Check if product has only default variant
    const hasOnlyDefaultVariant = (form.dataset.hasOnlyDefaultVariant || productFormComponent?.dataset.hasOnlyDefaultVariant) === 'true';
    if (hasOnlyDefaultVariant) {
      return; // Allow normal submission for single-variant products
    }

    event.preventDefault();
    event.stopPropagation();

    const productHandle = form.dataset.productHandle || productFormComponent?.dataset.productHandle;
    if (!productHandle) {
      console.error('Collection quick-add: Product handle not found');
      return;
    }

    this.#triggerButton = button;
    this.openQuickAddModal(productHandle, form);
  }

  /**
   * Opens the quick-add modal with the product section
   * @param {string} productHandle - The product handle
   * @param {HTMLFormElement} form - The form element
   */
  async openQuickAddModal(productHandle, form) {
    // Check cache first
    const cacheKey = `product-${productHandle}`;
    let sectionContent = this.#cachedContent.get(cacheKey);

    if (!sectionContent) {
      // Fetch the section via section rendering endpoint
      const sectionHtml = await this.fetchQuickAddSection(productHandle);
      if (sectionHtml) {
        const sectionElement = sectionHtml.querySelector('.quick-add-product-section');
        if (sectionElement) {
          sectionContent = /** @type {Element} */ (sectionElement.cloneNode(true));
          this.#cachedContent.set(cacheKey, sectionContent);
        }
      }
    }

    if (sectionContent) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (sectionContent.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    // Open the existing quick-add modal
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent && 'showDialog' in dialogComponent) {
      // Store trigger button for focus restoration before opening
      if ('setTriggerButton' in dialogComponent && this.#triggerButton) {
        dialogComponent.setTriggerButton(this.#triggerButton);
      }
      dialogComponent.showDialog();
    }
  }

  /**
   * Fetches the quick-add section via section rendering endpoint
   * @param {string} productHandle - The product handle
   * @returns {Promise<Document | null>}
   */
  async fetchQuickAddSection(productHandle) {
    if (!productHandle) return null;

    // Abort previous fetch if still pending
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const sectionUrl = `/products/${productHandle}?section_id=quick-add-product`;
      const response = await fetch(sectionUrl, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch quick-add section: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        console.error('Error fetching quick-add section:', error);
        return null;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick-add modal with section content
   * @param {Element} sectionContent - The section content element
   */
  async updateQuickAddModal(sectionContent) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!sectionContent || !modalContent) return;

    // Clear existing content
    modalContent.innerHTML = '';

    // Append the section content directly
    modalContent.appendChild(sectionContent);

    // Initialize any components that need it
    this.#initializeComponents(modalContent);
  }

  /**
   * Initializes components in the modal
   * @param {Element} container - The container element
   */
  #initializeComponents(container) {
    // Components should auto-initialize via custom elements
    // But we can trigger any necessary setup here if needed
    const productFormComponents = container.querySelectorAll('product-form-component');
    productFormComponents.forEach((component) => {
      // Component should already be initialized via custom element definition
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionQuickAddHandler();
  });
} else {
  new CollectionQuickAddHandler();
}
